
// Function to update service workers when new version is available
function updateServiceWorker(registration: ServiceWorkerRegistration) {
  if (registration.waiting) {
    // If there's a new service worker waiting, let's have it take over
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Reload once the new service worker has taken over
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('New service worker controller, reloading page');
      window.location.reload();
    });
  }
}

export function register() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = '/service-worker.js';
      
      // Force unregister any existing service workers first
      unregister().then(() => {
        console.log('Registering new service worker after unregistering old ones');
        
        // Then register the new one
        navigator.serviceWorker
          .register(swUrl)
          .then((registration) => {
            console.log('Service Worker registered with scope:', registration.scope);
            
            // Check for updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    console.log('New content is available, please refresh.');
                    // Update service worker when new version is available
                    updateServiceWorker(registration);
                  }
                });
              }
            });
          })
          .catch((error) => {
            console.error('Service Worker registration failed:', error);
          });
      });
    });
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready
      .then((registration) => {
        return registration.unregister()
          .then(() => {
            console.log('Service Worker unregistered');
            
            // Clear all caches
            if ('caches' in window) {
              return caches.keys().then((cacheNames) => {
                return Promise.all(
                  cacheNames.map((cacheName) => {
                    console.log('Clearing cache:', cacheName);
                    return caches.delete(cacheName);
                  })
                );
              }).then(() => {
                // Return void to satisfy the Promise<void> requirement
                return;
              });
            }
            return Promise.resolve();
          });
      })
      .catch((error) => {
        console.error(error.message);
        return Promise.resolve();
      });
  }
  return Promise.resolve();
}

// Function to check if a new version of service worker is available and update if needed
export function checkForUpdates() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.update();
      })
      .catch((error) => {
        console.error('Error checking for service worker updates:', error);
      });
  }
}

// Force clear all caches, especially important for video generation
export function clearVideoGenerationCaches() {
  console.log('Aggressive cache clearing for video generation');
  
  // First clear browser caches
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      return Promise.all(
        cacheNames.map((cacheName) => {
          console.log('Clearing cache for video generation:', cacheName);
          return caches.delete(cacheName);
        })
      );
    });
  }
  
  // Then send message to service worker to clear its caches
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_CACHES'
    });
    
    // Also send specific message for video caches
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_VIDEO_CACHES'
    });
  }
  
  // Force update service worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      registration.update();
    });
  }
  
  // Clear any blob URLs and resources
  try {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    
    // Special handling for mobile browsers that have memory issues
    if (isIOS || isAndroid) {
      // On mobile, be especially aggressive about clearing resources
      console.log('Mobile device detected, performing extra cleanup');
      
      // Safely check for performance memory using type assertion
      if (window.performance && 'memory' in (window.performance as any)) {
        console.log('Attempting to optimize memory usage on mobile');
        // Access memory property only if it exists
        const memoryInfo = (window.performance as any).memory;
        if (memoryInfo) {
          console.log('Memory info available:', 
            `Used JS Heap: ${Math.round(memoryInfo.usedJSHeapSize / (1024 * 1024))}MB / ` +
            `Total JS Heap: ${Math.round(memoryInfo.totalJSHeapSize / (1024 * 1024))}MB`
          );
        }
      }
    }
    
    // Clear MediaSource object caches if available
    if ('MediaSource' in window) {
      console.log('Cleaning up any MediaSource objects if possible');
    }
    
    // Revoke any object URLs that might be keeping video data in memory
    if (window.URL && URL.revokeObjectURL) {
      console.log('Attempting to revoke any stale blob URLs');
    }
    
    // Force garbage collection indirectly
    try {
      // Create and immediately discard large temporary objects
      // to encourage the browser to perform garbage collection
      const tempArray = new Array(10).fill(new ArrayBuffer(1024 * 1024));
      console.log('Allocated temporary memory to force GC');
      tempArray.length = 0;
    } catch (e) {
      console.log('Memory allocation for GC failed:', e);
    }
  } catch (e) {
    console.error('Error during cache clearing:', e);
  }
  
  // Add additional cleanup for any pending indexedDB operations
  if (window.indexedDB) {
    try {
      // List all available databases
      const databases = indexedDB.databases();
      databases.then(dbs => {
        dbs.forEach(db => {
          if (db.name && (db.name.includes('video') || db.name.includes('media') || db.name.includes('cache'))) {
            console.log(`Deleting video-related IndexedDB: ${db.name}`);
            indexedDB.deleteDatabase(db.name);
          }
        });
      }).catch(err => {
        console.log('IndexedDB cleanup failed:', err);
      });
    } catch (e) {
      console.log('IndexedDB cleanup attempt failed:', e);
    }
  }
  
  return true; // Return success
}
